/** @jsxImportSource @emotion/react */
import { Box, useTheme } from '@mui/material'
import { FancySuspense } from '@app/components/Atoms/FancySuspense'
import { useMemo } from 'react'
import { elementExposureList, elementExposureRow } from './ExposureHistogram.styles'
import { usePrioritisationContext } from '@app/contexts/ModuleContexts/PrioritisationContext'
import { Tooltip } from '@app/components/Atoms/Tooltip/Tooltip'

interface ExposureHistogramProps {
  hazardGroupName: string
}

export const ExposureHistogram = ({ hazardGroupName }: ExposureHistogramProps) => {
  const { exposurePercIsLoading, exposurePercIsInitialized, exposurePercByElement } =
    usePrioritisationContext()
  const theme = useTheme()

  const sortedElementExposure = useMemo(() => {
    if (!exposurePercByElement[hazardGroupName]) return []
    return exposurePercByElement[hazardGroupName].sort((a, b) => b.percentage - a.percentage)
  }, [exposurePercByElement, hazardGroupName])

  if (!exposurePercByElement || Object.keys(exposurePercByElement).length === 0) {
    return (
      <>
        <FancySuspense isLoading={true} variant="box" />
        <FancySuspense isLoading={true} variant="horizontalBarChartInverse" />
      </>
    )
  }

  return (
    <>
      <FancySuspense isLoading={!exposurePercIsInitialized} variant="horizontalBarChartInverse">
        {exposurePercByElement[hazardGroupName] && (
          <Box css={elementExposureList(exposurePercIsLoading)}>
            {sortedElementExposure.map((element, i) => {
              return (
                <Box
                  css={elementExposureRow({
                    index: i,
                    percentage: element.percentage,
                    theme,
                  })}
                  key={element.element}
                >
                  <Box
                    className="td"
                    sx={{
                      minWidth: 160,
                      maxWidth: 160,
                      flexShrink: '0 !important',
                      width: 160,
                      justifyContent: 'flex-end !important',
                    }}
                  >
                    <Tooltip
                      title={element.element}
                      placement="right-end"
                      leaveDelay={0}
                      followCursor
                      arrow={false}
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: theme.palette.yellow.light,
                            color: theme.palette.black.main,
                            fontSize: '10px',
                            borderRadius: 0,
                            padding: '2px 4px',
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        {element.element}
                      </Box>
                    </Tooltip>
                  </Box>
                  <Box className="td" sx={{ flexGrow: 1, fontSize: '10px' }}>
                    <Box className="bar" />
                    <Box className={`bar-label`}>
                      <Box className="percentage">{element.percentage}% </Box>
                      <Box className="metrics">
                        {element.exposure}
                        {element.unit}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </FancySuspense>
    </>
  )
}
